
import Clarity from '@microsoft/clarity';
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { CLARITY_PROJECT_ID } from '../../../project_config';
import { api } from "../../../utils/api";
import {
	canShowDetailManagement,
} from "../../../utils/detailManagementAccess";
import { LoginHelper } from "../../../utils/LoginHelper";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import Footer from "../../common/footer/Footer";
import { FooterButton } from "../../common/footer/FooterButton";
import Header from "../../common/header/Header";
import Skeleton from "../../common/loading/Skeleton";
import ActionSection from "./action/ActionSection";
import EnrollSection from "./enroll/EnrollSection";
import JoinSection from "./join/JoinSection";
import WebinarSummarySection from "./summary/WebinarSummarySection";
import TimerSection from "./timer/TimerSection";
import { isWebinarSpeaker } from "./webinarAccess";

export default function WebinarDetail() {
    Clarity.init(CLARITY_PROJECT_ID);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
    const params = useParams();
    const webinarId = params.webinarId;
    const [webinar, setWebinar] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const joinSectionRef = useRef(null);

    const linkDataList = [
        { label: 'خانه', href: '/' },
        { label: 'وبینارها', href: '/webinar_list/' },
        { label: webinar?.title || 'وبینار' },
    ];

    useEffect(() => {
        fetchWebinar();
    }, [webinarId]);

    useEffect(() => {
        const refreshAfterPayment = () => {
            if (document.visibilityState === "visible") {
                fetchWebinar();
            }
        };
        window.addEventListener("focus", refreshAfterPayment);
        document.addEventListener("visibilitychange", refreshAfterPayment);
        return () => {
            window.removeEventListener("focus", refreshAfterPayment);
            document.removeEventListener("visibilitychange", refreshAfterPayment);
        };
    }, [webinarId]);

    useEffect(() => {
        document.title = `وبینار ${webinar?.title || ''} | همراهتیم`;
    }, [webinar]);

    function fetchWebinar() {
        setIsLoading(true);
        api.webinarDetail({
            token: LoginHelper.getToken(),
            webinar_id: webinarId,
        }).then(result => {
            setWebinar(result);
            setIsLoading(false);
        }).catch((err) => {
            setIsLoading(false);
            if (err && err.data) {
                setErrorMessage(err.data.message);
            }
        })
    }

    const handleFinishTimer = () => {
        joinSectionRef.current?.reload();
    }

    const canManage =
        webinar &&
        canShowDetailManagement(false, isWebinarSpeaker(webinar));

    const sidebarBlocks = webinar ? (
        <>
            <EnrollSection webinar={webinar} />
            {canManage && <ActionSection webinar={webinar} />}
        </>
    ) : null;

    return (
        <>
            <Helmet>
                <title>وبینار {webinar?.title || ''} | همراهتیم</title>
                <meta name="description" content={`وبینار ${webinar?.title || ''} همراهتیم`} />
            </Helmet>

            <div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
                <div>
                    <Header isDesktop={isDesktop} />
                    <div className="bg-white">
                        <div className="mx-auto w-full max-w-[1200px] px-[10px] md:px-4">
                            <Breadcrumb linkDataList={linkDataList} />
                        </div>
                    </div>

                    <div className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-5 overflow-x-hidden px-[10px] pb-8 pt-6 md:px-4 md:pt-8">
                        {isLoading && (
                            <Skeleton variant="detail" type="webinar" />
                        )}

                        {errorMessage && !webinar && (
                            <div className="rounded-[14px] border border-red-200 bg-white px-4 py-6 text-center font-Vazir-400 text-[#cc0000]">
                                {errorMessage}
                            </div>
                        )}

                        {webinar && (
                            isDesktop ? (
                                <div className="flex flex-row items-start gap-5">
                                    <div className="flex min-w-0 flex-1 flex-col gap-5">
                                        <WebinarSummarySection webinar={webinar} />
                                        <TimerSection webinar={webinar} onFinish={handleFinishTimer} />
                                        <JoinSection ref={joinSectionRef} webinar={webinar} />
                                    </div>
                                    <div className="sticky top-4 flex w-[300px] shrink-0 flex-col gap-4">
                                        {sidebarBlocks}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-5">
                                    <WebinarSummarySection webinar={webinar} />
                                    <EnrollSection webinar={webinar} />
                                    {canManage && <ActionSection webinar={webinar} />}
                                    <TimerSection webinar={webinar} onFinish={handleFinishTimer} />
                                    <JoinSection ref={joinSectionRef} webinar={webinar} />
                                </div>
                            )
                        )}
                    </div>
                </div>
                <Footer isDesktop={isDesktop} activeButton={FooterButton.WEBINARS} />
            </div>
        </>
    )
}
